import { BlockStack, RangeSlider, SkeletonBodyText, Text } from '@shopify/polaris';
import { useGetUserPlanQuery } from '@/redux/api/api.caller';
import { Subscription } from '@/constants/enum';
import { closestAllowedValue, getValidQuotaValuesBySubscription } from './config';
import { memo } from 'react';
import { capitalizeFirstLetter } from '@/helpers';
import { getSubscriptionText } from '../../config';

interface QuotaSliderProps {
  subscription: Subscription;
  totalQuota: number;
  setTotalQuota: (value: number) => void;
}

const QuotaSlider = ({ subscription, totalQuota, setTotalQuota }: QuotaSliderProps) => {
  const { data, isLoading } = useGetUserPlanQuery({});
  const validQuotaValuesBySubscription = getValidQuotaValuesBySubscription(data?.data.allPlans || [], subscription);

  const handleChangeSlider = (value: number) => {
    // Find nearest allowed value
    const nearestValue = closestAllowedValue(value, validQuotaValuesBySubscription);

    // Update both total quota and extra quota
    if (totalQuota !== nearestValue) {
      setTotalQuota(nearestValue);
    }
  };

  // Format display value based on subscription type
  const formatDisplayValue = (value: number) => {
    const formattedValue = value.toLocaleString();
    return `${capitalizeFirstLetter(getSubscriptionText(subscription, subscription === Subscription.Annually))} order quota: ${formattedValue}`;
  };

  return (
    <BlockStack gap="200">
      <Text variant="headingMd" as="h3">
        Find your best deal based on {getSubscriptionText(subscription, subscription === Subscription.Annually)} orders
      </Text>
      {isLoading ? (
        <SkeletonBodyText lines={1} />
      ) : (
        <RangeSlider
          label={formatDisplayValue(totalQuota)}
          min={validQuotaValuesBySubscription[0]}
          max={validQuotaValuesBySubscription[validQuotaValuesBySubscription.length - 1]}
          value={totalQuota}
          onChange={handleChangeSlider}
          output
        />
      )}
    </BlockStack>
  );
};

export default memo(QuotaSlider);
